import React, { useEffect, useMemo, useState } from 'react';
import { useSAData } from './useSAData';
import { inr, pct } from '@/components/po-grn/format';
import { F } from './formulas';

const PERIODS = [['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', 'Last 7 Days'], ['30d', 'Last 30 Days']];

const Card = ({ label, value, sub, testId, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={testId}
    title={onClick ? 'Click to drill down' : undefined}
    className="group text-left rounded-xl border border-orange-200 bg-gradient-to-br from-orange-50 to-white px-5 py-4 shadow-sm transition-[box-shadow,transform] enabled:hover:-translate-y-0.5 enabled:hover:shadow-md enabled:cursor-pointer">
    <p className="text-xs font-semibold uppercase tracking-wider text-stone-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-stone-900 group-enabled:group-hover:text-orange-700 group-enabled:group-hover:underline underline-offset-4">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-stone-500">{sub}</p>}
  </button>
);
const PRESET = { today: 'today', yesterday: 'yesterday', '7d': 'last7', '30d': 'last30' };
const dmy = (d) => (d ? d.split('-').reverse().join('-') : '');

const stores = (n) => `${n ?? 0} store${n === 1 ? '' : 's'}`;

// Headline cards with their own period switch; the top date filter is ignored, other filters apply.
export const QuickKpis = ({ params, onDrill }) => {
  const gFrom = params?.date_from;
  const gTo = params?.date_to;
  const [period, setPeriod] = useState(gFrom || gTo ? 'custom' : 'today');
  // Global date filter changed → cards follow it until a switch option is picked again.
  useEffect(() => { setPeriod(gFrom || gTo ? 'custom' : 'today'); }, [gFrom, gTo]);
  const q = useMemo(() => {
    const { date_from, date_to, month, ...rest } = params || {};
    return period === 'custom' ? { ...rest, date_from, date_to, period } : { ...rest, period };
  }, [params, period]);
  const { data, loading } = useSAData('/quick-kpis', q);
  const label = PERIODS.find(([k]) => k === period)?.[1] || 'Date filter';
  const go = (patch, tab, name) => data && onDrill({
    tab, patch: { ...(period === 'custom' ? {} : { date_from: data.date_from, date_to: data.date_to, date_preset: PRESET[period], month: '' }),
      flag: ['sales'], ...patch },
  }, `${name} · ${label}`);
  const byType = (t, name) => data?.[t]?.store_types?.length
    ? () => go({ store_types: data[t].store_types }, 'stores', name) : undefined;
  return (
    <section className="rounded-xl border border-orange-100 bg-white p-4" data-testid="sa-quick-kpis">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-stone-800">
          Headline · {data ? (data.date_from ? `${dmy(data.date_from)} to ${dmy(data.date_to)}` : `All dates to ${dmy(data.date_to)}`) : '…'}
          {period === 'custom' && <span className="ml-2 rounded-full bg-violet-100 px-2 py-0.5 text-[11px] font-medium text-violet-700" data-testid="sa-quick-following-filter">following date filter</span>}
        </h3>
        <div className="flex rounded-full border border-orange-200 bg-orange-50 p-0.5" data-testid="sa-quick-period">
          {PERIODS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setPeriod(k)} data-testid={`sa-quick-period-${k}`}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${period === k ? 'bg-stone-900 text-white' : 'text-stone-600 hover:bg-orange-100'}`}>
              {l}
            </button>
          ))}
        </div>
      </div>
      <div className={`grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3 transition-opacity ${loading ? 'opacity-50' : ''}`}>
        <Card label="Total Sales · FIFO Stores" value={inr(data?.fifo?.sales)} sub={stores(data?.fifo?.stores)} testId="sa-quick-fifo" onClick={byType('fifo', 'FIFO store sales')} />
        <Card label="Total Sales · FICO Stores" value={inr(data?.fico?.sales)} sub={stores(data?.fico?.stores)} testId="sa-quick-fico" onClick={byType('fico', 'FICO store sales')} />
        <Card label="Current Margin %" value={pct(data?.margin_pct)} sub={F.current} testId="sa-quick-margin-pct" onClick={data ? () => go({}, 'oltowns', 'Current margin %') : undefined} />
        <Card label="Current Margin Value" value={inr(data?.margin)} sub={`coverage ${data?.margin_coverage_pct ?? '—'}% of sales`} testId="sa-quick-margin-value" onClick={data ? () => go({}, 'transactions', 'Current margin value') : undefined} />
      </div>
    </section>
  );
};
